import { ref, computed, watch } from '@vue/composition-api'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import store from '@/store'
import i18n from '@/libs/i18n'
import useNotifications from '@/composables/useNotifications'
import usePermissionMatrix from './usePermissionMatrix'
import {
  MODULE_NAV_KEYS, HEADER_ORDER, MODULE_HEADER_KEYS, ACTIONS,
} from './permissionModules'

// Permisos efectivos de UN worker puntual: permission_default[worker.privileges] fusionado
// con worker_permission_override[worker_id] (si existe), acción por acción — donde hay
// override gana el override, si no queda el default. `worker` es el ref reactivo que ya
// usa la edición de worker (Organization → Workers → Edit → {id}); acá solo se leen
// `_id`/`privileges`, nunca se escriben.
export default function useWorkerPermissionOverride(worker) {
  const { getItem } = useLambda()
  const { showErrorMessage } = useNotifications()
  const { role } = store.state?.userStore?.userData
  const {
    viewMode, setViewMode: setViewModeMatrix, toggleAction: toggleActionMatrix, resetTarget,
  } = usePermissionMatrix()

  const isLoading = ref(true)
  const defaultDoc = ref({ modules: {} })
  const overrideDoc = ref({ modules: {} })
  const expanded = ref(HEADER_ORDER.reduce((acc, headerKey) => ({ ...acc, [headerKey]: false }), {}))

  const workerId = computed(() => worker.value?._id || null)
  const target = computed(() => ({
    collection: 'worker_permission_override',
    query: { worker_id: oid(workerId.value) },
    upsert: true,
  }))

  // Trae permission_default para un privilegio puntual (se re-llama cuando cambia el
  // select de Privilegios en el formulario, incluso antes de guardar) + el override del
  // worker (una sola vez, no depende del privilegio elegido).
  const fetchDefault = async privilege => {
    if (!privilege) {
      defaultDoc.value = { modules: {} }
      return
    }
    const item = await getItem({ collection: 'permission_default', query: { privilege } })
    defaultDoc.value = item || { modules: {} }
  }

  const fetchOverride = async () => {
    if (!workerId.value) {
      overrideDoc.value = { modules: {} }
      return
    }
    const item = await getItem({ collection: 'worker_permission_override', query: { worker_id: oid(workerId.value) } })
    overrideDoc.value = item || { modules: {} }
  }

  const fetchAll = async () => {
    isLoading.value = true
    try {
      await Promise.all([fetchDefault(worker.value?.privileges), fetchOverride()])
    } catch (error) {
      console.log(error)
    } finally {
      isLoading.value = false
    }
  }

  // Mismo criterio de orden que usePermissionsList.js (pantalla de Permisos por defecto):
  // las claves del documento en Mongo vienen en el orden en que se fueron cargando (ej. vía
  // $set), no en el orden lógico del registro — hay que reordenar contra MODULE_HEADER_KEYS.
  const moduleKeys = computed(() => {
    const registryOrder = Object.keys(MODULE_HEADER_KEYS)
    return [...Object.keys(defaultDoc.value.modules || {})]
      .sort((a, b) => registryOrder.indexOf(a) - registryOrder.indexOf(b))
  })

  // Una fila por módulo. `cell` es el valor EFECTIVO (override si existe, si no default);
  // `overridden` marca, acción por acción, si ese valor puntual vino del override — es lo
  // que decide el color (warning) en el template.
  const rows = computed(() => moduleKeys.value.map(moduleKey => {
    const defaultEntry = defaultDoc.value.modules?.[moduleKey] || {}
    const overrideEntry = overrideDoc.value.modules?.[moduleKey] || {}

    const cell = {}
    const overridden = {}
    ACTIONS.forEach(action => {
      const isOverridden = Object.prototype.hasOwnProperty.call(overrideEntry, action)
      cell[action] = isOverridden ? overrideEntry[action] : (defaultEntry[action] || null)
      overridden[action] = isOverridden
    })

    return {
      moduleKey,
      moduleNavKey: MODULE_NAV_KEYS[moduleKey] || { key: moduleKey },
      headerKey: MODULE_HEADER_KEYS[moduleKey] || null,
      cell,
      overridden,
    }
  }))

  const groups = computed(() => HEADER_ORDER
    .map(headerKey => ({ headerKey, rows: rows.value.filter(row => row.headerKey === headerKey) }))
    .filter(group => group.rows.length))

  const allExpanded = computed(() => Object.values(expanded.value).every(Boolean))

  const toggleAll = () => {
    const next = !allExpanded.value
    Object.keys(expanded.value).forEach(headerKey => { expanded.value[headerKey] = next })
  }

  // ¿Este worker tiene aunque sea un override puntual guardado? Lo usa el caller para
  // decidir si avisar con un modal antes de cambiar de privilegio, y para deshabilitar
  // "Restablecer a valores por defecto" cuando no hay nada que restablecer.
  const hasOverrides = computed(() => Object.keys(overrideDoc.value.modules || {})
    .some(moduleKey => Object.keys(overrideDoc.value.modules[moduleKey] || {}).length > 0))

  // Editar permisos de un worker es exclusivo de consultant (decisión de negocio — evita que
  // un Manager/Supervisor/Employee con acceso a Ver/Editar Workers pueda tocar los overrides
  // de permisos de un colaborador, aunque la sección esté oculta en la UI). El v-if en
  // Edit.vue ya oculta toda la sección para el resto; esto es la segunda capa, por si se
  // llaman estas funciones directo.
  const setViewMode = (moduleKey, mode) => {
    if (role !== 'consultant') {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    const row = rows.value.find(r => r.moduleKey === moduleKey)
    setViewModeMatrix(overrideDoc.value, target.value, moduleKey, mode, row?.cell)
  }

  const toggleAction = (moduleKey, action) => {
    if (role !== 'consultant') {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }
    const row = rows.value.find(r => r.moduleKey === moduleKey)
    if (!row) return
    toggleActionMatrix(overrideDoc.value, target.value, moduleKey, action, row.cell)
  }

  // "Restablecer a valores por defecto": borra worker_permission_override entero para este
  // worker. Misma acción que dispara la confirmación de cambio de privilegio.
  const resetOverride = async () => {
    if (role !== 'consultant') {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return false
    }
    const ok = await resetTarget(target.value)
    if (ok) overrideDoc.value = { modules: {} }
    return ok
  }

  watch(workerId, () => fetchAll())

  return {
    isLoading,
    groups,
    expanded,
    allExpanded,
    toggleAll,
    viewMode,
    setViewMode,
    toggleAction,
    hasOverrides,
    resetOverride,
    fetchDefault,
    fetchAll,
  }
}
